// src/pages/carbon-offset-dashboard/index.jsx
import React, { useState, useEffect, useMemo } from 'react';
import Icon from 'components/AppIcon';
import CarbonOffsetMetrics from './components/CarbonOffsetMetrics';
import OffsetPurchaseModal from './components/OffsetPurchaseModal';
import OffsetHistoryTable from './components/OffsetHistoryTable';
import CarbonFootprintChart from './components/CarbonFootprintChart';
import SustainabilityGoals from './components/SustainabilityGoals';

const CarbonOffsetDashboard = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('last30days');
  const [isOffsetModalOpen, setIsOffsetModalOpen] = useState(false);
  const [selectedShipmentForOffset, setSelectedShipmentForOffset] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [sustainabilityGoals, setSustainabilityGoals] = useState({
    monthlyOffsetTarget: 500,
    carbonNeutralityTarget: '2025-12-31',
    offsetPercentage: 75
  });

  // Mock carbon offset data
  const mockOffsetData = {
    totalEmissions: {
      current: 1247.5,
      previous: 1189.2,
      unit: 'tonnes CO2e'
    },
    offsetPurchased: {
      current: 934.5,
      previous: 856.3,
      unit: 'tonnes CO2e'
    },
    offsetPercentage: {
      current: 75.1,
      previous: 72.0
    },
    carbonCredits: {
      available: 245.8,
      pending: 89.2,
      unit: 'tonnes CO2e'
    },
    offsetHistory: [
      {
        id: 1,
        date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        shipmentId: 'MSK-2024-001847',
        customer: 'Global Electronics Corp',
        amount: 24.5,
        cost: 490.00,
        offsetType: 'Forestry',
        certificateId: 'VCS-001847-2024',
        status: 'verified',
        project: 'Amazon Rainforest Conservation'
      },
      {
        id: 2,
        date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        shipmentId: 'COSCO-2024-002156',
        customer: 'Automotive Parts Ltd',
        amount: 18.2,
        cost: 364.00,
        offsetType: 'Renewable Energy',
        certificateId: 'VCS-002156-2024',
        status: 'verified',
        project: 'Wind Farm Project Mexico'
      },
      {
        id: 3,
        date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        shipmentId: 'MSC-2024-005632',
        customer: 'Pharmaceutical Dynamics',
        amount: 12.4,
        cost: 248.00,
        offsetType: 'Blue Carbon',
        certificateId: 'VCS-005632-2024',
        status: 'verified',
        project: 'Mangrove Restoration Indonesia'
      },
      {
        id: 4,
        date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
        shipmentId: 'HAPAG-2024-004789',
        customer: 'Tech Solutions Global',
        amount: 15.6,
        cost: 312.00,
        offsetType: 'Direct Air Capture',
        certificateId: 'VCS-004789-2024',
        status: 'pending',
        project: 'Climeworks DAC Facility'
      }
    ],
    chartData: {
      emissions: [
        { month: 'Jan', emissions: 1180, offset: 820 },
        { month: 'Feb', emissions: 1220, offset: 890 },
        { month: 'Mar', emissions: 1156, offset: 845 },
        { month: 'Apr', emissions: 1298, offset: 934 },
        { month: 'May', emissions: 1247, offset: 935 }
      ]
    }
  };

  // Mock eligible shipments for offset
  const mockEligibleShipments = [
    {
      id: 1,
      trackingId: 'MSK-2024-001847',
      customer: 'Global Electronics Corp',
      carbonFootprint: { total: 24.5, unit: 'tonnes CO2e' },
      estimatedOffsetCost: 490.00,
      route: 'Shanghai, China → Los Angeles, USA'
    },
    {
      id: 4,
      trackingId: 'HAPAG-2024-004789',
      customer: 'Tech Solutions Global',
      carbonFootprint: { total: 15.6, unit: 'tonnes CO2e' },
      estimatedOffsetCost: 312.00,
      route: 'Singapore → Vancouver, Canada'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handlePurchaseOffset = (shipment) => {
    setSelectedShipmentForOffset(shipment);
    setIsOffsetModalOpen(true);
  };

  const handleOffsetPurchaseComplete = (offsetData) => {
    // Handle successful offset purchase
    console.log('Offset purchased:', offsetData);
    setIsOffsetModalOpen(false);
    setSelectedShipmentForOffset(null);
    setLastUpdate(new Date());
  };

  const timeRangeOptions = [
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'last90days', label: 'Last 90 Days' },
    { value: 'thisyear', label: 'This Year' }
  ];

  const offsetPercentage = (mockOffsetData.offsetPurchased.current / mockOffsetData.totalEmissions.current) * 100;
  const remainingEmissions = mockOffsetData.totalEmissions.current - mockOffsetData.offsetPurchased.current;

  return (
    <div className="min-h-screen bg-background">
      {/* Header Section */}
      <div className="bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">Carbon Offset Management</h1>
              <p className="text-text-secondary mt-1">
                Track, purchase, and manage carbon offsets for your shipping operations
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <select
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(e.target.value)}
                className="px-3 py-2 border border-border rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {timeRangeOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Clock" size={16} />
                <span>Last updated: {lastUpdate.toLocaleTimeString()}</span>
              </div>
              
              <button
                onClick={() => setLastUpdate(new Date())}
                className="p-2 text-text-secondary hover:text-primary hover:bg-primary-50 rounded-md transition-colors duration-150"
                title="Refresh data"
              >
                <Icon name="RefreshCw" size={18} />
              </button>
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <button
              onClick={() => setIsOffsetModalOpen(true)}
              className="bg-success text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-success-600 transition-colors duration-150 flex items-center space-x-2"
            >
              <Icon name="Leaf" size={16} />
              <span>Purchase Offset</span>
            </button>
            
            <button className="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-150 flex items-center space-x-2">
              <Icon name="Download" size={16} />
              <span>Export Report</span>
            </button>
            
            <button className="border border-border text-text-primary px-4 py-2 rounded-md text-sm font-medium hover:bg-surface-50 transition-colors duration-150 flex items-center space-x-2">
              <Icon name="Settings" size={16} />
              <span>Settings</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Metrics Cards */}
        <CarbonOffsetMetrics 
          data={mockOffsetData}
          timeRange={selectedTimeRange}
        />

        {/* Charts and Goals Row */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Carbon Footprint Chart */}
          <div className="xl:col-span-2">
            <CarbonFootprintChart 
              data={mockOffsetData.chartData}
              timeRange={selectedTimeRange}
            />
          </div>

          {/* Sustainability Goals */}
          <div className="xl:col-span-1">
            <SustainabilityGoals 
              goals={sustainabilityGoals}
              currentData={mockOffsetData}
              onGoalUpdate={setSustainabilityGoals}
            />
          </div>
        </div>

        {/* Offset History Table */}
        <OffsetHistoryTable 
          offsetHistory={mockOffsetData.offsetHistory}
          timeRange={selectedTimeRange}
          onPurchaseOffset={handlePurchaseOffset}
          eligibleShipments={mockEligibleShipments}
        />
      </div>

      {/* Offset Purchase Modal */}
      {isOffsetModalOpen && (
        <OffsetPurchaseModal
          shipment={selectedShipmentForOffset}
          eligibleShipments={mockEligibleShipments}
          onClose={() => {
            setIsOffsetModalOpen(false);
            setSelectedShipmentForOffset(null);
          }}
          onPurchaseComplete={handleOffsetPurchaseComplete}
        />
      )}
    </div>
  );
};

export default CarbonOffsetDashboard;